import React, { useState, useEffect, useMemo } from 'react';

const emptyLinkForm = {
  title: '',
  url: '',
  duration_seconds: ''
};

const emptyResourceForm = {
  title: '',
  url: '',
  resourceType: 'file',
  description: ''
};

const resourceTypeLabels = {
  file: 'Archivo',
  drive: 'Google Drive',
  github: 'GitHub',
  link: 'Enlace externo'
};

const resourceExamples = [
  {
    label: 'Ejemplo Drive',
    values: {
      title: 'Plantillas del proyecto en Drive',
      url: 'https://drive.google.com/file/d/1EjemploDrive/view?usp=sharing',
      resourceType: 'drive',
      description: 'Carpeta compartida con plantillas y documentos del curso.'
    }
  },
  {
    label: 'Ejemplo GitHub',
    values: {
      title: 'Repositorio del proyecto final',
      url: 'https://github.com/openai/example-course-repo',
      resourceType: 'github',
      description: 'Codigo fuente base y ejercicios del proyecto final.'
    }
  },
  {
    label: 'Ejemplo PDF',
    values: {
      title: 'Guia PDF descargable',
      url: 'https://example.com/recursos/guia-del-curso.pdf',
      resourceType: 'file',
      description: 'Manual de apoyo para repasar la clase.'
    }
  }
];

const extractEmbeddableUrl = (rawValue) => {
  if (!rawValue || typeof rawValue !== 'string') {
    return '';
  }

  const trimmedValue = rawValue.trim();
  const iframeMatch = trimmedValue.match(/<iframe[^>]+src=["']([^"']+)["']/i);
  return iframeMatch?.[1] || trimmedValue;
};

const parseResourceKind = (lecture) => {
  const description = lecture?.description || '';
  const match = description.match(/^\[resource:([a-z]+)\]/i);
  return match?.[1]?.toLowerCase() || 'file';
};

const cleanResourceDescription = (lecture) =>
  (lecture?.description || '').replace(/^\[resource:[a-z]+\]\s*/i, '').trim();

const buildResourceDescription = (resourceType, description) => {
  const normalizedDescription = description?.trim() || `Recurso tipo ${resourceTypeLabels[resourceType] || 'Archivo'}`;
  return `[resource:${resourceType}] ${normalizedDescription}`;
};

const UdemyFilmVideos = ({ course, updateCourse, sectionId = null, compact = false }) => {
  const [activeTab, setActiveTab] = useState('upload');
  const [videos, setVideos] = useState([]);
  const [linkForm, setLinkForm] = useState(emptyLinkForm);
  const [resourceForm, setResourceForm] = useState(emptyResourceForm);
  const [editingLectureId, setEditingLectureId] = useState(null);
  const [editingResourceId, setEditingResourceId] = useState(null);
  const [isSavingLink, setIsSavingLink] = useState(false);
  const [isSavingResource, setIsSavingResource] = useState(false);

  const apiBase = useMemo(() => {
    const apiOrigin = process.env.REACT_APP_API_ORIGIN
      || (process.env.REACT_APP_API_URL
        ? process.env.REACT_APP_API_URL.replace(/\/api\/?$/, '')
        : 'http://localhost:5000');
    return apiOrigin.endsWith('/') ? apiOrigin.slice(0, -1) : apiOrigin;
  }, []);

  const targetSections = useMemo(() => {
    const allSections = Array.isArray(course?.sections) ? course.sections : [];
    return sectionId
      ? allSections.filter((section) => section.id === sectionId)
      : allSections;
  }, [course, sectionId]);

  const currentSection = targetSections[0] || null;
  const currentLectures = currentSection?.lectures || [];
  const videoLectures = currentLectures.filter((lecture) => lecture.content_type === 'video');
  const resourceLectures = currentLectures.filter((lecture) => lecture.content_type === 'file' || lecture.content_type === 'article');

  useEffect(() => {
    setVideos(
      targetSections.flatMap((section) =>
        (section.lectures || [])
          .filter((lecture) => lecture.content_type === 'video')
          .map((lecture) => ({
            id: lecture.id,
            sectionId: section.id,
            name: lecture.title || 'Sin titulo',
            size: '',
            status: 'completed',
            url: lecture.videoUrl || lecture.content_url || '',
            duration: lecture.duration ?? lecture.duration_seconds ?? null,
            lecture
          }))
      )
    );
  }, [targetSections]);

  const prettySize = (bytes) => {
    if (!Number.isFinite(bytes)) return '';
    const mb = bytes / (1024 * 1024);
    if (mb < 1024) return `${mb.toFixed(2)} MB`;
    const gb = mb / 1024;
    return `${gb.toFixed(2)} GB`;
  };

  const ensureTargetSection = () => {
    if (!course) return { sectionId: null, courseFixed: null };

    const courseFixed = {
      ...course,
      sections: Array.isArray(course.sections) ? [...course.sections] : []
    };

    const desiredSection = sectionId
      ? courseFixed.sections.find((section) => section.id === sectionId)
      : courseFixed.sections[0];

    if (desiredSection) {
      if (!Array.isArray(desiredSection.lectures)) {
        desiredSection.lectures = [];
      }
      return { sectionId: desiredSection.id, courseFixed };
    }

    if (courseFixed.sections.length === 0) {
      courseFixed.sections.push({
        id: Date.now(),
        title: 'Videos Subidos',
        lectures: []
      });
    }

    return { sectionId: courseFixed.sections[0].id, courseFixed };
  };

  const uploadSingleFile = (file, targetSectionId) =>
    new Promise((resolve, reject) => {
      const form = new FormData();
      form.append('video', file);
      form.append('title', file.name.replace(/\.[^.]+$/, ''));

      const url = `${apiBase}/api/sections/${targetSectionId}/videos`;
      const xhr = new XMLHttpRequest();
      xhr.open('POST', url, true);
      xhr.withCredentials = true;

      xhr.upload.onprogress = (evt) => {
        if (evt.lengthComputable) {
          const pct = Math.round((evt.loaded / evt.total) * 100);
          setVideos((prev) =>
            prev.map((video) =>
              video.__tempId === file.__tempId ? { ...video, progress: pct } : video
            )
          );
        }
      };

      xhr.onreadystatechange = () => {
        if (xhr.readyState !== 4) return;

        let payload = {};
        try {
          payload = JSON.parse(xhr.responseText || '{}');
        } catch {
          payload = {};
        }

        if (xhr.status >= 200 && xhr.status < 300) {
          const data = payload?.data || payload;
          resolve({
            id: data.id || Date.now(),
            section_id: data.sectionId || targetSectionId,
            title: data.title || file.name.replace(/\.[^.]+$/, ''),
            content_url: data.url || data.secure_url || data.content_url || '',
            duration_seconds: data.duration ?? data.duration_seconds ?? null
          });
          return;
        }

        reject(new Error(payload?.message || `Error ${xhr.status} al subir ${file.name}`));
      };

      xhr.onerror = () => reject(new Error(`Fallo de red subiendo ${file.name}`));
      xhr.send(form);
    });

  const persistCourse = async (nextCourse) => {
    const savedCourse = await Promise.resolve(updateCourse(nextCourse));
    return savedCourse || nextCourse;
  };

  const buildUpdatedCourse = (mutator) => {
    const nextCourse = {
      ...course,
      sections: (course?.sections || []).map((section) => ({
        ...section,
        lectures: Array.isArray(section.lectures) ? [...section.lectures] : []
      }))
    };

    mutator(nextCourse);
    return nextCourse;
  };

  const resetLinkEditor = () => {
    setEditingLectureId(null);
    setLinkForm(emptyLinkForm);
  };

  const resetResourceEditor = () => {
    setEditingResourceId(null);
    setResourceForm(emptyResourceForm);
  };

  const handleExternalLinkSave = async () => {
    if (!currentSection) {
      alert('Selecciona una seccion valida primero.');
      return;
    }

    if (!linkForm.title.trim() || !linkForm.url.trim()) {
      alert('Completa el titulo y la URL del video.');
      return;
    }

    setIsSavingLink(true);
    try {
      const nextCourse = buildUpdatedCourse((draftCourse) => {
        draftCourse.sections = draftCourse.sections.map((section) => {
          if (section.id !== currentSection.id) return section;

          const lectures = Array.isArray(section.lectures) ? [...section.lectures] : [];

          if (editingLectureId) {
            return {
              ...section,
              lectures: lectures.map((lecture) =>
                lecture.id === editingLectureId
                  ? {
                      ...lecture,
                      title: linkForm.title.trim(),
                      content_url: extractEmbeddableUrl(linkForm.url),
                      duration_seconds: Number(linkForm.duration_seconds) || 0,
                      duration: Number(linkForm.duration_seconds) || 0,
                      content_type: 'video',
                      description: lecture.description || 'Video externo'
                    }
                  : lecture
              )
            };
          }

          const nextPosition = lectures.length + 1;
          return {
            ...section,
            lectures: [
              ...lectures,
              {
                id: Date.now(),
                title: linkForm.title.trim(),
                content_type: 'video',
                content_url: extractEmbeddableUrl(linkForm.url),
                duration_seconds: Number(linkForm.duration_seconds) || 0,
                duration: Number(linkForm.duration_seconds) || 0,
                position: nextPosition,
                is_preview: false,
                description: 'Video externo'
              }
            ]
          };
        });
      });

      await persistCourse(nextCourse);
      resetLinkEditor();
    } catch (error) {
      console.error('Error al guardar enlace externo:', error);
      alert('No se pudo guardar el enlace del video.');
    } finally {
      setIsSavingLink(false);
    }
  };

  const handleResourceSave = async () => {
    if (!currentSection) {
      alert('Selecciona una seccion valida primero.');
      return;
    }

    if (!resourceForm.title.trim() || !resourceForm.url.trim()) {
      alert('Completa el titulo y la URL del recurso.');
      return;
    }

    setIsSavingResource(true);
    try {
      const nextCourse = buildUpdatedCourse((draftCourse) => {
        draftCourse.sections = draftCourse.sections.map((section) => {
          if (section.id !== currentSection.id) return section;

          const lectures = Array.isArray(section.lectures) ? [...section.lectures] : [];

          if (editingResourceId) {
            return {
              ...section,
              lectures: lectures.map((lecture) =>
                lecture.id === editingResourceId
                  ? {
                      ...lecture,
                      title: resourceForm.title.trim(),
                      content_type: resourceForm.resourceType === 'github' ? 'article' : 'file',
                      content_url: resourceForm.url.trim(),
                      duration_seconds: 0,
                      duration: 0,
                      description: buildResourceDescription(resourceForm.resourceType, resourceForm.description)
                    }
                  : lecture
              )
            };
          }

          const nextPosition = lectures.length + 1;
          return {
            ...section,
            lectures: [
              ...lectures,
              {
                id: Date.now(),
                title: resourceForm.title.trim(),
                content_type: resourceForm.resourceType === 'github' ? 'article' : 'file',
                content_url: resourceForm.url.trim(),
                duration_seconds: 0,
                duration: 0,
                position: nextPosition,
                is_preview: false,
                description: buildResourceDescription(resourceForm.resourceType, resourceForm.description)
              }
            ]
          };
        });
      });

      await persistCourse(nextCourse);
      resetResourceEditor();
    } catch (error) {
      console.error('Error al guardar recurso:', error);
      alert('No se pudo guardar el recurso.');
    } finally {
      setIsSavingResource(false);
    }
  };

  const startEditingLecture = (lecture) => {
    setEditingLectureId(lecture.id);
    setLinkForm({
      title: lecture.title || '',
      url: lecture.videoUrl || lecture.content_url || '',
      duration_seconds: String(lecture.duration_seconds ?? lecture.duration ?? '')
    });
    setActiveTab('links');
  };

  const startEditingResource = (lecture) => {
    setEditingResourceId(lecture.id);
    setResourceForm({
      title: lecture.title || '',
      url: lecture.content_url || '',
      resourceType: parseResourceKind(lecture),
      description: cleanResourceDescription(lecture)
    });
    setActiveTab('resources');
  };

  const removeLecture = async (lectureId) => {
    try {
      const nextCourse = buildUpdatedCourse((draftCourse) => {
        draftCourse.sections = draftCourse.sections.map((section) => ({
          ...section,
          lectures: (section.lectures || []).filter((lecture) => lecture.id !== lectureId)
        }));
      });

      await persistCourse(nextCourse);
      if (editingLectureId === lectureId) {
        resetLinkEditor();
      }
      if (editingResourceId === lectureId) {
        resetResourceEditor();
      }
    } catch (error) {
      console.error('Error al eliminar leccion:', error);
      alert('No se pudo eliminar la clase.');
    }
  };

  const loadResourceExample = (values) => {
    setResourceForm(values);
    setEditingResourceId(null);
  };

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const ensured = ensureTargetSection();
    if (!ensured.sectionId) {
      alert('No hay una seccion valida para subir videos.');
      e.target.value = '';
      return;
    }

    const tempItems = files.map((file) => {
      const __tempId = `${Date.now()}_${Math.random().toString(36).slice(2)}`;
      Object.defineProperty(file, '__tempId', { value: __tempId });
      return {
        __tempId,
        id: __tempId,
        sectionId: ensured.sectionId,
        name: file.name,
        size: prettySize(file.size),
        status: 'pending',
        progress: 0,
        url: '',
        duration: null
      };
    });

    setVideos((prev) => [...tempItems, ...prev]);

    for (const file of files) {
      try {
        setVideos((prev) =>
          prev.map((video) =>
            video.__tempId === file.__tempId ? { ...video, status: 'processing' } : video
          )
        );

        const uploaded = await uploadSingleFile(file, ensured.sectionId);
        const nextCourse = buildUpdatedCourse((draftCourse) => {
          draftCourse.sections = draftCourse.sections.map((section) => {
            if (section.id !== ensured.sectionId) return section;

            const nextPosition = (section.lectures || []).length + 1;
            return {
              ...section,
              lectures: [
                ...(section.lectures || []),
                {
                  id: uploaded.id,
                  title: uploaded.title,
                  content_type: 'video',
                  content_url: uploaded.content_url,
                  duration_seconds: uploaded.duration_seconds || 0,
                  duration: uploaded.duration_seconds || 0,
                  position: nextPosition,
                  is_preview: false,
                  description: `Video subido: ${uploaded.title}`
                }
              ]
            };
          });
        });

        await persistCourse(nextCourse);

        setVideos((prev) =>
          prev.map((video) =>
            video.__tempId === file.__tempId
              ? {
                  ...video,
                  id: uploaded.id,
                  name: uploaded.title,
                  status: 'completed',
                  progress: 100,
                  url: uploaded.content_url,
                  duration: uploaded.duration_seconds,
                  __tempId: undefined
                }
              : video
          )
        );
      } catch (error) {
        console.error(error);
        setVideos((prev) =>
          prev.map((video) =>
            video.__tempId === file.__tempId
              ? { ...video, status: 'error', error: error.message }
              : video
          )
        );
      }
    }

    e.target.value = '';
  };

  if (!course) {
    return <div className="text-center py-4 text-gray-600">Selecciona un curso.</div>;
  }

  if (compact && !currentSection) {
    return <div className="text-sm text-gray-500">No hay una seccion valida para editar.</div>;
  }

  return (
    <div className={compact ? 'mt-3' : 'bg-white rounded-lg shadow-sm p-6'}>
      {!compact && <h2 className="text-xl font-bold mb-4">Videos del curso: {course.title}</h2>}

      <div className="flex border-b border-gray-200 mb-4">
        <button
          className={`px-4 py-2 font-medium ${activeTab === 'upload' ? 'text-purple-600 border-b-2 border-purple-600' : 'text-gray-500'}`}
          onClick={() => setActiveTab('upload')}
          type="button"
        >
          Subir videos
        </button>
        <button
          className={`px-4 py-2 font-medium ${activeTab === 'links' ? 'text-purple-600 border-b-2 border-purple-600' : 'text-gray-500'}`}
          onClick={() => setActiveTab('links')}
          type="button"
        >
          Enlaces externos
        </button>
        <button
          className={`px-4 py-2 font-medium ${activeTab === 'resources' ? 'text-purple-600 border-b-2 border-purple-600' : 'text-gray-500'}`}
          onClick={() => setActiveTab('resources')}
          type="button"
        >
          Recursos
        </button>
      </div>

      {activeTab === 'upload' ? (
        <div>
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center mb-4">
            <input
              type="file"
              id={`video-upload-${sectionId || 'course'}`}
              multiple
              accept="video/*"
              onChange={handleFileChange}
              className="hidden"
            />
            <label
              htmlFor={`video-upload-${sectionId || 'course'}`}
              className="cursor-pointer flex flex-col items-center justify-center"
            >
              <span className="text-lg font-medium text-gray-700">Haz clic para subir videos a esta seccion</span>
              <span className="text-sm text-gray-500">Formatos soportados: MP4, MOV, AVI</span>
            </label>
          </div>

          {videos.length > 0 && (
            <ul className="divide-y divide-gray-200">
              {videos
                .filter((video) => !sectionId || video.sectionId === sectionId)
                .map((video) => (
                  <li key={video.id} className="py-3 flex items-center justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{video.name}</div>
                      <div className="text-sm text-gray-500">{video.size}</div>
                      {typeof video.progress === 'number' && video.status !== 'completed' && (
                        <div className="mt-2 w-full bg-gray-200 rounded h-2 overflow-hidden">
                          <div className="bg-purple-600 h-2" style={{ width: `${video.progress}%` }} />
                        </div>
                      )}
                    </div>
                    <span className="px-2 py-1 text-xs rounded bg-gray-100 text-gray-800">
                      {video.status}
                    </span>
                  </li>
                ))}
            </ul>
          )}
        </div>
      ) : activeTab === 'links' ? (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <input
              type="text"
              value={linkForm.title}
              onChange={(e) => setLinkForm((prev) => ({ ...prev, title: e.target.value }))}
              placeholder="Titulo de la clase"
              className="px-3 py-2 border rounded"
            />
            <input
              type="text"
              value={linkForm.url}
              onChange={(e) => setLinkForm((prev) => ({ ...prev, url: e.target.value }))}
              placeholder="URL, link compartido o iframe de YouTube/OneDrive"
              className="px-3 py-2 border rounded md:col-span-2"
            />
            <input
              type="number"
              min="0"
              value={linkForm.duration_seconds}
              onChange={(e) => setLinkForm((prev) => ({ ...prev, duration_seconds: e.target.value }))}
              placeholder="Duracion en segundos"
              className="px-3 py-2 border rounded"
            />
          </div>

          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleExternalLinkSave}
              disabled={isSavingLink}
              className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-60"
            >
              {editingLectureId ? 'Guardar cambios' : 'Agregar enlace'}
            </button>
            {editingLectureId && (
              <button
                type="button"
                onClick={resetLinkEditor}
                className="px-4 py-2 border rounded hover:bg-gray-50"
              >
                Cancelar
              </button>
            )}
          </div>

          <p className="text-xs text-gray-500">
            Para OneDrive, el enlace compartido normal puede no ser un video directo. Si falla, pega el codigo de insercion `iframe` o el enlace de `Embed` generado desde OneDrive.
          </p>

          <div className="rounded border">
            <div className="px-4 py-3 border-b bg-gray-50 text-sm font-medium">
              {currentSection ? `Clases de la seccion: ${currentSection.title}` : 'Clases del curso'}
            </div>

            {videoLectures.length === 0 ? (
              <div className="p-4 text-sm text-gray-500">Todavia no hay enlaces de video en esta seccion.</div>
            ) : (
              <ul className="divide-y">
                {videoLectures.map((lecture) => (
                  <li key={lecture.id} className="p-4 flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <div className="font-medium">{lecture.title}</div>
                      <div className="text-sm text-gray-500 break-all">{lecture.content_url || lecture.videoUrl}</div>
                      <div className="text-xs text-gray-400 mt-1">
                        Duracion: {lecture.duration_seconds || lecture.duration || 0}s
                      </div>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <button
                        type="button"
                        onClick={() => startEditingLecture(lecture)}
                        className="px-3 py-1 text-sm border rounded hover:bg-gray-50"
                      >
                        Editar
                      </button>
                      <button
                        type="button"
                        onClick={() => removeLecture(lecture.id)}
                        className="px-3 py-1 text-sm text-red-600 border border-red-200 rounded hover:bg-red-50"
                      >
                        Eliminar
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="rounded-xl border border-blue-100 bg-blue-50 p-4">
            <p className="text-sm font-semibold text-blue-800 mb-3">Ejemplos rapidos</p>
            <div className="flex flex-wrap gap-2">
              {resourceExamples.map((example) => (
                <button
                  key={example.label}
                  type="button"
                  onClick={() => loadResourceExample(example.values)}
                  className="rounded-full border border-blue-200 bg-white px-3 py-1 text-sm text-blue-700 hover:bg-blue-100"
                >
                  {example.label}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <input
              type="text"
              value={resourceForm.title}
              onChange={(e) => setResourceForm((prev) => ({ ...prev, title: e.target.value }))}
              placeholder="Titulo del recurso"
              className="px-3 py-2 border rounded"
            />
            <select
              value={resourceForm.resourceType}
              onChange={(e) => setResourceForm((prev) => ({ ...prev, resourceType: e.target.value }))}
              className="px-3 py-2 border rounded"
            >
              <option value="file">Archivo / PDF</option>
              <option value="drive">Google Drive</option>
              <option value="github">GitHub</option>
              <option value="link">Otro enlace</option>
            </select>
            <input
              type="text"
              value={resourceForm.url}
              onChange={(e) => setResourceForm((prev) => ({ ...prev, url: e.target.value }))}
              placeholder="URL del recurso, carpeta o repositorio"
              className="px-3 py-2 border rounded md:col-span-2"
            />
            <textarea
              value={resourceForm.description}
              onChange={(e) => setResourceForm((prev) => ({ ...prev, description: e.target.value }))}
              placeholder="Descripcion breve del recurso"
              className="px-3 py-2 border rounded md:col-span-2 min-h-[96px]"
            />
          </div>

          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleResourceSave}
              disabled={isSavingResource}
              className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-60"
            >
              {editingResourceId ? 'Guardar recurso' : 'Agregar recurso'}
            </button>
            {editingResourceId && (
              <button
                type="button"
                onClick={resetResourceEditor}
                className="px-4 py-2 border rounded hover:bg-gray-50"
              >
                Cancelar
              </button>
            )}
          </div>

          <p className="text-xs text-gray-500">
            Puedes pegar enlaces compartidos de Google Drive, repositorios o archivos de GitHub, PDFs, documentos o cualquier URL externa de apoyo para la seccion.
          </p>

          <div className="rounded border">
            <div className="px-4 py-3 border-b bg-gray-50 text-sm font-medium">
              {currentSection ? `Recursos de la seccion: ${currentSection.title}` : 'Recursos del curso'}
            </div>

            {resourceLectures.length === 0 ? (
              <div className="p-4 text-sm text-gray-500">Todavia no hay recursos agregados en esta seccion.</div>
            ) : (
              <ul className="divide-y">
                {resourceLectures.map((lecture) => {
                  const resourceKind = parseResourceKind(lecture);
                  return (
                    <li key={lecture.id} className="p-4 flex items-start justify-between gap-4">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <div className="font-medium">{lecture.title}</div>
                          <span className="rounded-full bg-slate-100 px-2 py-1 text-xs text-slate-700">
                            {resourceTypeLabels[resourceKind] || 'Archivo'}
                          </span>
                        </div>
                        <div className="text-sm text-gray-500 break-all mt-1">{lecture.content_url}</div>
                        <div className="text-xs text-gray-400 mt-1">{cleanResourceDescription(lecture)}</div>
                      </div>
                      <div className="flex gap-2 shrink-0">
                        <a
                          href={lecture.content_url}
                          target="_blank"
                          rel="noreferrer"
                          className="px-3 py-1 text-sm border rounded hover:bg-gray-50"
                        >
                          Abrir
                        </a>
                        <button
                          type="button"
                          onClick={() => startEditingResource(lecture)}
                          className="px-3 py-1 text-sm border rounded hover:bg-gray-50"
                        >
                          Editar
                        </button>
                        <button
                          type="button"
                          onClick={() => removeLecture(lecture.id)}
                          className="px-3 py-1 text-sm text-red-600 border border-red-200 rounded hover:bg-red-50"
                        >
                          Eliminar
                        </button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default UdemyFilmVideos;
